import { useMemo } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import type { OrderSideValue } from '@apna/shared'
import { expiryLabel } from '@/lib/fmt'
import { Notice } from '@/components/Notice'
import { ChainHeader } from '@/screens/terminal/ChainHeader'
import { ChainRow } from '@/screens/terminal/ChainRow'
import { ExpiryStrip } from '@/screens/terminal/ExpiryStrip'
import { LOAD_MORE_STRIKES, type ChainLadder } from '@/screens/terminal/useChainLadder'
import { prefetchChain } from '@/feed/prefetchChain'
import type { BasketLegDto } from '@/types/basket'
import { tickStore } from '@/feed/tickStore'

/**
 * The ladder, in the builder — ROADMAP 9.66.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NOTHING HERE FIRES
 *
 * B and S on this ladder ADD TO THE BASKET. On the Terminal they place an
 * order on the click, with no confirmation (4.15) — which is why the builder
 * is its own surface rather than a mode on that screen. A button that means
 * one thing when a panel is open and another when it is closed is the single
 * most dangerous control this product could ship, and a separate screen
 * removes the question rather than answering it with a tint.
 *
 * They still READ as B and S, because that is the muscle memory, and they are
 * still red and green for the same reason. What changes is the label — "+B"
 * — the accessible name, and the fact that there is no account anywhere on
 * this side of the screen.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SAME ROW COMPONENT, NOT A COPY OF IT
 *
 * `ChainRow` already knows about the twelve columns, the ITM tint, the OI
 * bars, the greeks and the stale-cell rendering. Passing it a different
 * `onTrade` is the whole difference between the two screens — which is what
 * `onTrade` being a callback was always for.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export interface BasketChainProps {
  ladder: ChainLadder
  underlying: string
  /**
   * A click on B or S, as a LEG rather than four arguments.
   *
   * The expiry and the lot size are facts of the contract that was clicked
   * and this component is the one holding them — so it describes the leg and
   * the basket decides what to do with it. The alternative had the basket
   * reading the ladder, and the ladder reading the basket's underlying,
   * which is a cycle React resolves by lagging a render.
   */
  onAdd: (leg: BasketLegDto) => void
}

export function BasketChain({ ladder, underlying, onAdd }: BasketChainProps) {
  const {
    visible,
    hiddenAbove,
    hiddenBelow,
    showMoreAbove,
    showMoreBelow,
    expiries,
    expiry,
    setExpiry,
    loading,
    error,
    spot,
    atm,
    greeks,
    lotSize,
    bodyRef,
  } = ladder

  /*
   * A click with no expiry or no lot size cannot describe a contract, so it
   * does nothing rather than adding a leg with a guessed one. Both are
   * present the moment the catalogue has answered; before that the ladder
   * has no rows to click.
   */
  const add = (
    token: number,
    strike: number,
    optionType: 'CE' | 'PE',
    side: OrderSideValue,
  ): void => {
    if (!expiry || !(lotSize && lotSize > 0)) return
    onAdd({
      token,
      side,
      lots: 1,
      strike,
      optionType,
      expiry,
      tradingSymbol: `${underlying} ${strike} ${optionType}`,
      lotSize,
    })
  }

  /*
   * The OI scale for the bars — the largest on each side of the VISIBLE
   * window, so the tallest bar is the busiest strike on screen rather than
   * one scrolled out of view.
   *
   * Reads the store DIRECTLY rather than subscribing, the same way the
   * Terminal does: a bar's own cell re-renders on its own tick, and
   * re-deriving the maximum fifteen times a second would re-render the whole
   * table to move one bar a pixel. Recomputed when the window changes, which
   * is when the answer can actually change by much.
   */
  const { maxCeOi, maxPeOi } = useMemo(() => {
    let ce = 0
    let pe = 0
    for (const r of visible) {
      const c = r.ce?.brokerToken ? (tickStore.snapshot(Number(r.ce.brokerToken))?.oi ?? 0) : 0
      const p = r.pe?.brokerToken ? (tickStore.snapshot(Number(r.pe.brokerToken))?.oi ?? 0) : 0
      if (c > ce) ce = c
      if (p > pe) pe = p
    }
    return { maxCeOi: ce, maxPeOi: pe }
  }, [visible])

  return (
    <section className="bb-chain tr-card tr-chain" aria-label="Option chain">
      <ExpiryStrip
        expiries={expiries}
        value={expiry}
        onChange={setExpiry}
        onIntent={(iso) => prefetchChain(underlying, iso)}
      />

      {error && (
        <Notice tone="error" className="m-3">
          {error}
        </Notice>
      )}

      {/*
        MARKET ONLY, and there is no picker. This ladder is read to CHOOSE a
        strike — open interest, the price, delta, the chance of finishing in
        the money — and Greeks and Combined answer questions about a position
        already on. A setting whose other two values are never right on this
        screen is a setting that only costs a decision.
      */}
      <div className="tr-tbl tr-market" ref={bodyRef}>
        <ChainHeader
          mode="MARKET"
          expiry={expiry ? expiryLabel(expiry) : ''}
          onRefresh={greeks.refresh}
          showOi
        />

        {loading && visible.length === 0 && (
          <div className="tr-more text-[12px] text-muted-foreground">Loading the chain…</div>
        )}
        {!loading && !error && visible.length === 0 && (
          <div className="tr-more text-[12px] text-muted-foreground">
            No strikes for this expiry.
          </div>
        )}

        {hiddenAbove > 0 && (
          <div className="tr-more">
            <button type="button" className="tr-morebtn" onClick={showMoreAbove}>
              <ChevronUp size={14} />
              Show {Math.min(hiddenAbove, LOAD_MORE_STRIKES)} lower strikes
            </button>
          </div>
        )}

        {visible.map((row) => {
          const ceToken = row.ce?.brokerToken ? Number(row.ce.brokerToken) : null
          const peToken = row.pe?.brokerToken ? Number(row.pe.brokerToken) : null
          const solvedCe = ceToken === null ? null : (greeks.byToken.get(ceToken) ?? null)
          const solvedPe = peToken === null ? null : (greeks.byToken.get(peToken) ?? null)
          return (
            <ChainRow
              key={row.strike}
              mode="MARKET"
              strike={row.strike}
              ceToken={ceToken}
              peToken={peToken}
              spot={spot}
              isAtm={atm === row.strike}
              maxCeOi={maxCeOi}
              maxPeOi={maxPeOi}
              showOi
              onTrade={add}
              /*
               * NO ACCOUNT, and that is not an oversight. `tradeDisabled`
               * refuses a click that has nowhere to send; this ladder sends
               * nowhere by design, so the only thing that could disable a
               * button here is a strike that is not listed — which
               * `ChainRow` already handles from a null token.
               */
              tradeDisabled={false}
              action="ADD"
              ceIv={solvedCe?.iv ?? null}
              peIv={solvedPe?.iv ?? null}
              ceIvWhy={ceToken === null ? undefined : greeks.refusals.get(ceToken)}
              peIvWhy={peToken === null ? undefined : greeks.refusals.get(peToken)}
              ceDelta={solvedCe?.greeks.delta ?? null}
              peDelta={solvedPe?.greeks.delta ?? null}
              ceGreeks={solvedCe?.greeks ?? null}
              peGreeks={solvedPe?.greeks ?? null}
              ceItmPct={solvedCe?.pItm ?? null}
              peItmPct={solvedPe?.pItm ?? null}
            />
          )
        })}

        {hiddenBelow > 0 && (
          <div className="tr-more">
            <button type="button" className="tr-morebtn" onClick={showMoreBelow}>
              <ChevronDown size={14} />
              Show {Math.min(hiddenBelow, LOAD_MORE_STRIKES)} higher strikes
            </button>
          </div>
        )}
      </div>
    </section>
  )
}
